import * as R from 'ramda'
import { computed, ComputedRef } from 'vue'

import { Incident, SubParticipant } from '@collector/sportsapi-types'
import {
  useSubParticipants,
  useTeamSides,
} from '@desktop/views/Relation/composables'
import { IncidentPlayerOption } from '@desktop/views/Relation/types'

import {
  IsPlayer,
  IsPriorityPosition,
  sortSubParticipants,
} from '../../configuration'
import { injectPlayersConfig } from '../../provide-inject'
import { createIncidentPlayerOption } from './utils'

function getPlayersOptions(
  subParticipants: ComputedRef<SubParticipant[]>,
  isPlayer: IsPlayer | undefined,
  isPriorityPosition: IsPriorityPosition | undefined,
  compareFn?: (a: SubParticipant, b: SubParticipant) => number,
): ComputedRef<IncidentPlayerOption[]> {
  return computed(() =>
    R.pipe(
      isPlayer ? R.filter(isPlayer) : R.identity,
      compareFn
        ? (players: SubParticipant[]) => [...players].sort(compareFn)
        : R.partialRight(sortSubParticipants, [isPriorityPosition]),
      R.map(createIncidentPlayerOption),
    )(subParticipants.value),
  )
}

/**
 * Returns the home and away players options for incidents.
 *
 * @returns {Object} An object containing home and away players options.
 * @property {ComputedRef<IncidentPlayerOption[]>} homePlayersOptions - Computed reference to the home players options.
 * @property {ComputedRef<IncidentPlayerOption[]>} awayPlayersOptions - Computed reference to the away players options.
 */
export function useIncidentPlayers(): {
  homePlayersOptions: ComputedRef<IncidentPlayerOption[]>
  awayPlayersOptions: ComputedRef<IncidentPlayerOption[]>
  getIncidentPlayersOptions: (
    incident: Incident,
  ) => ComputedRef<IncidentPlayerOption[]>
} {
  // inject
  const playersConfig = injectPlayersConfig()

  // composables
  const { homeSubParticipants, awaySubParticipants } = useSubParticipants()
  const { getIncidentTeamSide } = useTeamSides()

  // computed
  const homePlayersOptions = getPlayersOptions(
    homeSubParticipants,
    playersConfig?.isPlayer,
    playersConfig?.isPriorityPosition,
    playersConfig?.compareFn,
  )
  const awayPlayersOptions = getPlayersOptions(
    awaySubParticipants,
    playersConfig?.isPlayer,
    playersConfig?.isPriorityPosition,
    playersConfig?.compareFn,
  )

  function getIncidentPlayersOptions(
    incident: Incident,
  ): ComputedRef<IncidentPlayerOption[]> {
    const teamSide = getIncidentTeamSide(incident)

    return teamSide === 'home' ? homePlayersOptions : awayPlayersOptions
  }

  return {
    homePlayersOptions,
    awayPlayersOptions,
    getIncidentPlayersOptions,
  }
}
